import React, { createContext, useContext, useEffect, useReducer, useCallback, useMemo } from 'react';
import { AuthServiceFactory } from '@/services/auth/AuthServiceFactory';
import type { IAuthService } from '@/services/auth/IAuthService';
import { useReferralClaim } from '@/hooks/useReferralClaim';
import { storage } from '@/utils/StorageManager';
import { referralStorage, STORAGE_KEYS } from '@/utils/referralStorage';
import { REFERRAL_STORAGE, PROVIDER_REGISTRATION_STORAGE, DIETICIAN_REGISTRATION_STORAGE, PATIENT_REGISTRATION_STORAGE } from '@/constants/storage';
import type { AuthState, AuthContextType, User, LoginFormData, SignUpFormData, AuthResponse } from '@/types/auth';
import { getLoggerWithPrefix } from '@/services';
import { apiService } from '@/services';

// Initialize auth service instance using factory
const authService: IAuthService = AuthServiceFactory.getInstance();
const MFA_AUTO_SEND_STORAGE_KEY = 'mfa:autoSendOnceDone:v1';

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SIGN_OUT' };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SIGN_OUT':
      return { ...initialState, isLoading: false };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const logger = getLoggerWithPrefix('AuthProvider');
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { claimReferral } = useReferralClaim();

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // Check if session exists and get user info
        const sessionExists = await authService.doesSessionExist();
        if (sessionExists) {
          const user = await authService.getUserInfo();
          dispatch({ type: 'SET_USER', payload: user });
        } else {
          dispatch({ type: 'SET_USER', payload: null });
        }
      } catch (error) {
        logger.error('Auth initialization error:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize authentication' });
      }
    };

    initializeAuth();
  }, []);

  // Sign in function
  const signIn = useCallback(async (formData: LoginFormData): Promise<AuthResponse> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await authService.signIn(formData);

      if (response.status === 'OK' && response.user) {
        dispatch({ type: 'SET_USER', payload: response.user });



        // Attempt referral claiming after successful patient login (non-blocking)
        // This runs synchronously but doesn't affect the login flow
        try {
          // Check localStorage for stored referral token first
          const storedTokenResult = storage.get<string>(REFERRAL_STORAGE.TOKEN);
          if (storedTokenResult.success && storedTokenResult.data) {
            logger.log('Attempting to claim referral token from localStorage after patient login...');
            // Use Promise.resolve().then() to make it non-blocking without setTimeout
            Promise.resolve().then(() => claimReferral(storedTokenResult.data)).catch(error => {
              // Silent error handling - don't disrupt the login experience
              logger.warn('Non-blocking referral claim from localStorage failed:', error);
            });
          }

          // Also check session storage for self-referral data
          const sessionReferralData = referralStorage.retrieve();
          if (sessionReferralData?.workflow?.finalReferral?.referral_token) {
            const referralToken = sessionReferralData.workflow.finalReferral.referral_token;
            logger.log('Attempting to auto-claim self-referral token from sessionStorage after patient login...');

            // Use Promise.resolve().then() to make it non-blocking without setTimeout
            Promise.resolve().then(async () => {
              try {
                const autoClaimRequest = {
                  referralToken,
                  patientEmail: response.user?.email,
                  patientId: response.user?.id,
                  patientName: sessionReferralData.workflow.finalReferral?.registration_prefill_data.patient_name,
                };

                const autoClaimResponse = await apiService.selfReferral.autoClaimReferral(autoClaimRequest);

                if (autoClaimResponse.success) {
                  logger.log('Self-referral auto-claim successful: ', autoClaimResponse.message);
                  // Clean up session storage after successful claim
                  referralStorage.cleanup();
                } else {
                  logger.warn('Self-referral auto-claim failed:', autoClaimResponse.message);
                }
              } catch (error) {
                logger.warn('Self-referral auto-claim error:', error);
              }
            }).catch(error => {
              // Silent error handling - don't disrupt the login experience
              logger.warn('Non-blocking self-referral auto-claim failed:', error);
            });
          }
        } catch (error) {
          // Silent error handling - don't disrupt the login experience
          logger.warn('Non-blocking referral claim failed:', error);
        }

      } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        dispatch({ type: 'SET_ERROR', payload: 'Invalid email or password' });
      } else if (response.status === 'FIELD_ERROR') {
        const errorMessage = response.formFields?.map(f => f.error).join(', ') || 'Invalid form data';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      }

      return response;
    } catch (error) {
      logger.error('Login error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An unexpected error occurred during sign in' });
      return { status: 'FIELD_ERROR' as const, formFields: [{ id: 'general', error: 'Network error' }] };
    }
  }, [claimReferral]);

  // Sign up function
  const signUp = useCallback(async (formData: SignUpFormData): Promise<AuthResponse> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await authService.signUp(formData);

      if (response.status === 'OK' && response.user) {
        dispatch({ type: 'SET_USER', payload: response.user });
      } else if (response.status === 'FIELD_ERROR') {
        const errorMessage = response.formFields?.map(f => f.error).join(', ') || 'Invalid form data';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      }

      return response;
    } catch (error) {
      logger.error('Sign up error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An unexpected error occurred during sign up' });
      return { status: 'FIELD_ERROR' as const, formFields: [{ id: 'general', error: 'Network error' }] };
    }
  }, []);

  // Sign out function
  const signOut = useCallback(async () => {
    // Clear all stored data using StorageManager
    const localStorageKeysToRemove = [
      REFERRAL_STORAGE.WORKFLOW,
      REFERRAL_STORAGE.TOKEN,
      PROVIDER_REGISTRATION_STORAGE.WORKFLOW,
      PROVIDER_REGISTRATION_STORAGE.PROFILE_FORM,
      PROVIDER_REGISTRATION_STORAGE.REGISTRATION_FORM,
      DIETICIAN_REGISTRATION_STORAGE.WORKFLOW,
      PATIENT_REGISTRATION_STORAGE.WORKFLOW,
    ];

    const sessionStorageKeysToRemove = [
      STORAGE_KEYS.REFERRAL_DATA,
      STORAGE_KEYS.REFERRAL_CONTEXT,
    ];

    storage.removeMultiple(localStorageKeysToRemove);
    storage.removeMultiple(sessionStorageKeysToRemove, 'sessionStorage');

    // Reset MFA auto-send behavior on logout
    try {
      window.localStorage?.removeItem(MFA_AUTO_SEND_STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await authService.signOut();
      dispatch({ type: 'SIGN_OUT' });
    } catch (error) {
      logger.error('Sign out error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to sign out' });
    }
  }, []);

  // Refresh session function
  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const success = await authService.refreshSession();
      if (success) {
        const user = await authService.getUserInfo();
        dispatch({ type: 'SET_USER', payload: user });
      } else {
        dispatch({ type: 'SIGN_OUT' });
      }
      return success;
    } catch (error) {
      logger.error('Refresh session error:', error);
      dispatch({ type: 'SIGN_OUT' });
      return false;
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Memoize context value for performance
  const contextValue: AuthContextType = useMemo(() => ({
    ...state,
    signIn,
    signUp,
    signOut,
    refreshSession,
    clearError,
  }), [state, signIn, signUp, signOut, refreshSession, clearError]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}